import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Appointment } from '@salonomy/types';
import { api, unwrap } from '../../lib/api.js';
import { moneyWhole } from '../../lib/format.js';
import {
  addDays,
  isoDateInZone,
  todayInZone,
  weekdayInZone,
  zonedDayStart,
} from '../../lib/time.js';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Monday-anchored week view: one query for the whole week, bucketed by salon day. */
export function WeekStrip({
  anchor,
  tz,
  onPickDay,
}: {
  anchor: string;
  tz: string;
  onPickDay: (date: string) => void;
}) {
  const offset = (weekdayInZone(anchor, tz) + 6) % 7; // days since Monday
  const weekStart = addDays(anchor, -offset);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const today = todayInZone(tz);

  const { data: appts = [] } = useQuery({
    queryKey: ['appointments', 'week', weekStart],
    enabled: Boolean(tz),
    queryFn: async (): Promise<Appointment[]> => {
      const fromIso = zonedDayStart(weekStart, tz).toISOString();
      const toIso = zonedDayStart(addDays(weekStart, 7), tz).toISOString();
      return unwrap(
        await api.GET('/v1/appointments', { params: { query: { from: fromIso, to: toIso } } }),
      );
    },
  });

  const byDay = useMemo(() => {
    const map = new Map<string, { count: number; takings: number }>();
    for (const a of appts) {
      if (a.status === 'cancelled') continue;
      const d = isoDateInZone(new Date(a.startAt), tz);
      const cur = map.get(d) ?? { count: 0, takings: 0 };
      cur.count += 1;
      if (a.status === 'completed') cur.takings += a.priceQuoted;
      map.set(d, cur);
    }
    return map;
  }, [appts, tz]);

  return (
    <div className="diary__main">
      <div className="week">
        {days.map((d, i) => {
          const stat = byDay.get(d) ?? { count: 0, takings: 0 };
          const isToday = d === today;
          return (
            <button
              key={d}
              className={`week__day${isToday ? ' week__day--today' : ''}`}
              onClick={() => onPickDay(d)}
            >
              <span className="week__dow">{DOW[i]}</span>
              <span className="week__date">{d.slice(8)}</span>
              <span className="week__count">{stat.count ? `${stat.count} booked` : '—'}</span>
              {stat.takings ? (
                <span className="week__takings">{moneyWhole(stat.takings)}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
